import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import TrialDatePicker from "@/components/site/TrialDatePicker";
import { isBlockedTrialDay } from "@/lib/trialDates";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATES = [
  { v: "NSW", l: "New South Wales" },
  { v: "VIC", l: "Victoria" },
  { v: "QLD", l: "Queensland" },
  { v: "WA", l: "Western Australia" },
  { v: "SA", l: "South Australia" },
  { v: "TAS", l: "Tasmania" },
  { v: "ACT", l: "Australian Capital Territory" },
  { v: "NT", l: "Northern Territory" },
];

const TERRAINS = ["Beach & sand", "Bush tracks", "Outback / red dirt", "Farm / station", "All of the above"];

const TIME_SLOTS = ["Morning", "Afternoon", "Either"];

export default function TestDriveForm() {
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    preferred_trial_date: "",
    trial_time_slot: "",
    state: "",
    terrain: "",
    message: "",
    company: "", // honeypot — must stay empty
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [renderedAt] = useState(() => Date.now());

  const onChange = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e?.target?.value ?? e }));

  // If the state changes and the currently-picked date is now blocked by that
  // state's holidays, clear the date and nudge the user to re-pick.
  useEffect(() => {
    if (!form.state || !form.preferred_trial_date) return;
    const [y, m, d] = form.preferred_trial_date.split("-").map(Number);
    if (!y || !m || !d) return;
    if (isBlockedTrialDay(new Date(y, m - 1, d), form.state)) {
      setForm((f) => ({ ...f, preferred_trial_date: "" }));
      toast.info("That date is a public holiday in your state — please pick another.");
    }
  }, [form.state, form.preferred_trial_date]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.email || !form.phone || !form.preferred_trial_date || !form.trial_time_slot || !form.state) {
      toast.error("Please fill in all required fields.");
      return;
    }
    // Time-based bot guard: a real user takes at least a few seconds to fill the form
    if (Date.now() - renderedAt < 2500) {
      toast.error("Whoa there — slow down a sec and try again.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads/test-drive`, form);
      setDone(true);
      toast.success("Request received. A Terrainhopper specialist will call within 3 business days.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const status = err?.response?.status;
      let msg;
      if (status === 429) {
        msg = "Too many submissions. Please try again in an hour.";
      } else {
        msg = Array.isArray(detail) ? detail[0]?.msg : (detail || "Something went wrong. Please try again.");
      }
      toast.error(String(msg));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="test-drive" data-testid="test-drive" className="relative py-24 md:py-32 bg-foreground text-background overflow-hidden">
      <div className="mx-auto max-w-7xl px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
        <div className="lg:col-span-5">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-primary">
            Book a Trial
          </span>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl uppercase tracking-tight font-bold leading-[1.05]">
            Try it before <br /> you commit.
          </h2>
          <p className="mt-6 text-base md:text-lg text-background/70 leading-relaxed max-w-md">
            Trials are run by Overlander Mobility (Geelong, VIC) and travel across Australia by
            arrangement. Tell us where you are and where you want to ride — we&apos;ll be in
            touch within three business days.
          </p>
          <div
            data-testid="demo-pricing"
            className="relative mt-6 inline-flex items-baseline gap-2 bg-primary/15 border border-primary/40 rounded-sm px-4 py-3"
          >
            <span
              aria-hidden="true"
              data-testid="pricing-booked-out-banner"
              className="absolute -top-3 -right-4 z-10 rotate-[8deg] bg-background text-primary text-[10px] font-black uppercase tracking-[0.12em] px-2.5 py-1 rounded-sm shadow-lg border border-primary/40 pointer-events-none whitespace-nowrap"
            >
              &apos;26 Booked Out
            </span>
            <span className="text-xs uppercase tracking-[0.2em] font-bold text-background/90">Trials from</span>
            <span className="font-display text-2xl md:text-3xl font-black text-primary leading-none">$299</span>
          </div>
          <ul className="mt-8 space-y-3 text-sm text-background/80">
            {["Trials from $299, anywhere in Victoria", "Interstate trials quoted on request", "Full written quote and configuration", "NDIS / DVA documentation support"].map((b) => (
              <li key={b} className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-7">
          {done ? (
            <div
              data-testid="form-success"
              className="bg-background text-foreground rounded-sm p-10 border border-primary"
            >
              <CheckCircle2 className="h-10 w-10 text-primary" />
              <h3 className="mt-4 font-display text-2xl md:text-3xl font-bold uppercase tracking-tight">
                You&apos;re on the list.
              </h3>
              <p className="mt-3 text-muted-foreground">
                We&apos;ve sent your request through to the closest Terrainhopper specialist. Expect a
                call from an Australian number within three business days.
              </p>
              <Button
                data-testid="form-reset-btn"
                onClick={() => { setDone(false); setForm({ first_name: "", last_name: "", email: "", phone: "", preferred_trial_date: "", trial_time_slot: "", state: "", terrain: "", message: "", company: "" }); }}
                variant="outline"
                className="mt-6 rounded-sm uppercase tracking-wider font-bold"
              >
                Submit another
              </Button>
            </div>
          ) : (
            <form
              data-testid="test-drive-form"
              onSubmit={submit}
              className="bg-background text-foreground rounded-sm p-7 md:p-10 border border-background/20 space-y-5"
            >
              {/* Honeypot — visually hidden, off the tab order. Bots fill it; real users never see it. */}
              <div
                aria-hidden="true"
                style={{
                  position: "absolute",
                  left: "-9999px",
                  top: "auto",
                  width: 1,
                  height: 1,
                  overflow: "hidden",
                }}
              >
                <label htmlFor="om-company">Company (leave this empty)</label>
                <input
                  id="om-company"
                  name="company"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={form.company}
                  onChange={onChange("company")}
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field label="First name" required>
                  <Input data-testid="input-first-name" value={form.first_name} onChange={onChange("first_name")} placeholder="Jane" className="rounded-sm" />
                </Field>
                <Field label="Last name" required>
                  <Input data-testid="input-last-name" value={form.last_name} onChange={onChange("last_name")} placeholder="Citizen" className="rounded-sm" />
                </Field>
                <Field label="Email" required>
                  <Input data-testid="input-email" type="email" value={form.email} onChange={onChange("email")} placeholder="jane@example.com.au" className="rounded-sm" />
                </Field>
                <Field label="Phone" required>
                  <Input data-testid="input-phone" type="tel" value={form.phone} onChange={onChange("phone")} placeholder="04xx xxx xxx" className="rounded-sm" />
                </Field>
                <Field label="Preferred trial date" required>
                  <TrialDatePicker
                    testId="input-trial-date"
                    value={form.preferred_trial_date}
                    onChange={(iso) => setForm((f) => ({ ...f, preferred_trial_date: iso }))}
                    state={form.state}
                    placeholder="Pick a weekday"
                  />
                </Field>
                <Field label="Trial time slot" required>
                  <Select value={form.trial_time_slot} onValueChange={onChange("trial_time_slot")}>
                    <SelectTrigger data-testid="select-time-slot" className="rounded-sm">
                      <SelectValue placeholder="Morning or afternoon?" />
                    </SelectTrigger>
                    <SelectContent>
                      {TIME_SLOTS.map((s) => (
                        <SelectItem key={s} value={s} data-testid={`time-slot-${s.toLowerCase()}`}>
                          {s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="State" required>
                  <Select value={form.state} onValueChange={onChange("state")}>
                    <SelectTrigger data-testid="select-state" className="rounded-sm">
                      <SelectValue placeholder="Select state" />
                    </SelectTrigger>
                    <SelectContent>
                      {STATES.map((s) => (
                        <SelectItem key={s.v} value={s.v} data-testid={`state-${s.v}`}>
                          {s.v} — {s.l}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              </div>
              <Field label="Primary terrain (optional)">
                <Select value={form.terrain} onValueChange={onChange("terrain")}>
                  <SelectTrigger data-testid="select-terrain" className="rounded-sm">
                    <SelectValue placeholder="Where will you ride?" />
                  </SelectTrigger>
                  <SelectContent>
                    {TERRAINS.map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Tell us about your trip plans (optional)">
                <Textarea
                  data-testid="input-message"
                  value={form.message}
                  onChange={onChange("message")}
                  rows={4}
                  placeholder="Heading to Cape York in July, want to test soft sand…"
                  className="rounded-sm resize-none"
                />
              </Field>
              <Button
                data-testid="submit-test-drive"
                type="submit"
                disabled={loading}
                className="w-full rounded-sm uppercase tracking-wider font-bold py-6 text-sm group"
              >
                {loading ? "Sending..." : "Book my trial"}
                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Button>
              <p className="text-[11px] text-muted-foreground leading-relaxed">
                We respect your privacy. Your details go to a Terrainhopper Australia specialist only —
                never shared, never sold.
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[11px] uppercase tracking-[0.2em] font-bold text-muted-foreground">
        {label}{required && <span className="text-primary"> *</span>}
      </Label>
      {children}
    </div>
  );
}
